import { useEffect, useMemo, useRef } from 'react'
import { useProgress } from 'hooks/useProgress'

const TOTAL_POSTS = 10
const FETCHED_POSTS_THRESHOLD = 20
const ANALYZED_POSTS_THRESHOLD = 40
const REMAINING_POSTS_THRESHOLD = 100 - ANALYZED_POSTS_THRESHOLD

interface InsightsProgress {
  fetched_posts?: number
  analyzed_posts?: number
}

export const useIntegrationLoadingProgress = (insightsProgress?: InsightsProgress) => {
  const {
    start: startTwo,
    progress: progressTwo,
    isRunning: isRunningTwo,
  } = useProgress({
    linearProgress: false,
    speed: 200,
    immediateComplete: true,
  })

  const previousAnalyzedPostsRef = useRef<number | null>(null)

  // Trigger progressTwo when analyzed_posts reaches 10
  useEffect(() => {
    const currentAnalyzedPosts = insightsProgress?.analyzed_posts ?? 0
    const previousAnalyzedPosts = previousAnalyzedPostsRef.current

    // When analyzed_posts reaches 10, start progressTwo
    if (previousAnalyzedPosts !== null && currentAnalyzedPosts === TOTAL_POSTS && previousAnalyzedPosts < TOTAL_POSTS) {
      startTwo(0, 100)
    }

    previousAnalyzedPostsRef.current = currentAnalyzedPosts
  }, [insightsProgress?.analyzed_posts, startTwo])

  const loadingProgress = useMemo(() => {
    const fetchedPosts = insightsProgress?.fetched_posts ?? 0
    const analyzedPosts = insightsProgress?.analyzed_posts ?? 0

    if (analyzedPosts >= TOTAL_POSTS) {
      // analyzed_posts is complete, now use progressTwo for 60-100%
      if (isRunningTwo) {
        return ANALYZED_POSTS_THRESHOLD + Math.floor((progressTwo / 100) * REMAINING_POSTS_THRESHOLD)
      } else if (progressTwo >= 99) {
        // All complete
        return 99
      } else {
        // analyzed_posts is done, waiting for progressTwo to start
        return ANALYZED_POSTS_THRESHOLD
      }
    } else if (fetchedPosts >= TOTAL_POSTS) {
      // fetched_posts is complete, now use analyzed_posts for 30-60%
      // analyzed_posts (0-10) represents 30-60% of overall progress
      return FETCHED_POSTS_THRESHOLD + Math.floor((analyzedPosts / 10) * FETCHED_POSTS_THRESHOLD)
    } else {
      // Use fetched_posts for 0-30%
      // fetched_posts (0-10) represents 0-30% of overall progress
      return Math.floor((fetchedPosts / TOTAL_POSTS) * FETCHED_POSTS_THRESHOLD)
    }
  }, [insightsProgress?.fetched_posts, insightsProgress?.analyzed_posts, progressTwo, isRunningTwo])

  const loadingLabel = useMemo(() => {
    if (loadingProgress === 0) return 'Blaze is thinking'
    if (loadingProgress > 0 && loadingProgress < 20) return 'Fetching your posts'
    if (loadingProgress === 20) return 'Blaze is thinking'
    if (loadingProgress > 20 && loadingProgress < 40) return 'Analyzing performance'
    if (loadingProgress > 40 && loadingProgress < 60) return 'Finding patterns...'

    return 'Preparing insights'
  }, [loadingProgress])

  return {
    loadingProgress,
    loadingLabel,
  }
}
